import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

import PageContentWrapper from '../components/pageContentWrapper';
import {
  getAgent,
  updateUser,
  getUserOrg,
  createUser,
} from '../../service/userService';
import { getAccountOrgNodes } from '../../service/accountService';
import { PageContext } from '../components/page';
import { selectRelevantRole } from '../manageUsersContainer/responseProcessors';
import { flattenGetAccountOrgNodesResponse } from './responseProcessors';
import {
  DISPLAY_TO_ROLE,
  STATUS_TO_BOOL,
  BOOL_TO_STATUS,
} from '../../constants';
import EditUserForm from './EditUserForm';
import { CreateUserPath, ManageUserPath } from '../../appRoutes';

import { type DropdownOption } from '../../components/Dropdown';
import { getProperty } from '../../fsConfig';

const EditUserContainer: React.FC = () => {
  const {path, pushAlert } = useContext(PageContext);
  const navigate = useNavigate();
  const isCreateMode = path.includes(CreateUserPath);

  const user = getProperty('session', {});

  const [loading, setLoading] = useState(true);

  const { newUserId } = useParams<{ newUserId: string }>();
  const [username, setUsername] = useState<string>('');
  const [legacyId, setLegacyId] = useState<string>('');
  const [emailAddress, setEmailAddress] = useState<string>('');
  const [password, setPassword] = useState<string>('');

  const [status, setStatus] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [assignedOrgNodeId, setAssignedOrgNodeId] = useState<string>('');
  const [loggedInAndEditUserSame, setLoggedInAndEditUserSame] = useState<boolean>(false);
  const [assignedOrgOptions, setAssignedOrgOptions] = useState<DropdownOption<string>[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get the list of all valid orgs for this account to put into a select box for the user to pick from
        const accountOrgNodesResult = await getAccountOrgNodes();
        if (accountOrgNodesResult) {
          const flatOrgNodes = flattenGetAccountOrgNodesResponse(accountOrgNodesResult);
          setAssignedOrgOptions(flatOrgNodes);
        } else {
          pushAlert('danger', 'No response when attempting to get account organizations list.');
        }
  
        if (!isCreateMode) {
          // Get the user's details
          const agentResult = await getAgent(newUserId ? parseInt(newUserId) : null);
          setUsername(agentResult.userName);
          setEmailAddress(agentResult.email);
          setStatus(BOOL_TO_STATUS[agentResult.active ? 'true' : 'false']);
          setRole(selectRelevantRole(agentResult.roles));
  
          // Get the id of the assigned org of the user being edited to set as the starting value of the assignedOrgNodeId select box
          const userOrgResult = await getUserOrg(newUserId!);
          setAssignedOrgNodeId(userOrgResult.orgNodeId);
          setLegacyId(agentResult.legacyId);
          setLoggedInAndEditUserSame(user.id === agentResult.legacyId);
        }
      } catch (error) {
        if (error instanceof Error) {
          pushAlert('danger', `Error fetching data. ${error.message}`);
        } else {
          pushAlert('danger', 'An unknown error occurred.');
        }
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, []);

  const checkVariables = (variables: Record<string, any>): boolean => {
    for (const variable in variables) {
      // This is double-equals intentionally to catch nulls as well without being verbose
      if (variables[variable] == undefined) {
        pushAlert('danger', `Missing value "${variable}"`);
        return false;
      }
    }
    return true;
  };

  //only used if in edit mode
  const onSave = async (): Promise<void> => {
    //check variables, w/ keys being for display in error banner if necessary
    const hasVariables = checkVariables({
      'Admin newUserId': newUserId,
      Status: STATUS_TO_BOOL[status],
      Role: DISPLAY_TO_ROLE[role],
      'Assigned Organization': assignedOrgNodeId,
    });
    if (hasVariables && newUserId) {
      setLoading(true);
      try {
        await updateUser(
          newUserId,
          STATUS_TO_BOOL[status],
          DISPLAY_TO_ROLE[role],
          assignedOrgNodeId,
          emailAddress,
          username,
          legacyId
        );
        pushAlert('success', 'User info updated successfully.');
      } catch (error) {
        if (error instanceof Error) {
          pushAlert('danger', error.message);
        } else {
          pushAlert('danger', 'Error updating user info.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  //only used if in create mode
  const onCreate = async (): Promise<void> => {
    const hasVariables = checkVariables({
      username,
      password,
      emailAddress,
      role,
      status,
      assignedOrgNodeId,
    });
  
    if (hasVariables) {
      setLoading(true);
      try {
        await createUser(
          username,
          password,
          emailAddress,
          STATUS_TO_BOOL[status],
          DISPLAY_TO_ROLE[role],
          assignedOrgNodeId,
        );
        pushAlert('success', 'User created successfully.');
        navigate(ManageUserPath);
      } catch (err) {
        if (err instanceof Error) {
          if (err.message?.includes('Username already exists')) {
            pushAlert('danger', 'Username already exists.');
          } else {
            pushAlert('danger', err.message);
          }          
        } else {
          pushAlert('danger', 'Error creating user.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const contentWrapperTitle = `${isCreateMode ? 'CREATE' : 'EDIT'} USER`;

  return (
    <PageContentWrapper title={contentWrapperTitle} loading={loading}>
      <EditUserForm
        username={username}
        setUsername={setUsername}
        password={password}
        setPassword={setPassword}
        emailAddress={emailAddress}
        setEmailAddress={setEmailAddress}
        status={status}
        setStatus={setStatus}
        role={role}
        setRole={setRole}
        assignedOrgNodeId={assignedOrgNodeId}
        setAssignedOrgNodeId={setAssignedOrgNodeId}
        assignedOrgOptions={assignedOrgOptions}
        isCreateMode={isCreateMode}
        onSave={isCreateMode ? onCreate : onSave}
        loggedInAndEditUserSame={loggedInAndEditUserSame}
      />
    </PageContentWrapper>
  );
};

export default EditUserContainer;
